/**
 * App config fetched once from the backend at startup and memoised for
 * the rest of the browser session.
 *
 * Design goals:
 *  - **Single network round-trip** per session, regardless of how many
 *    components need config values.
 *  - **Non-blocking**: the fetch is kicked off eagerly on module import.
 *    Callers `await getAppConfig()` and receive the same promise so
 *    concurrent callers all resolve on the same request.
 *  - **Graceful degradation**: if the config endpoint 404s (older backend
 *    that predates the endpoint) we still let the app run and return an
 *    empty object so the caller can decide what to do.
 */
import { api } from "./api";

let _configPromise = null;

async function _fetchConfig() {
  try {
    const { data } = await api.get("/config");
    return data || {};
  } catch (err) {
    // Backend may be old or the endpoint might not exist yet — don't
    // break the app. Downstream code should treat missing keys as
    // "feature unavailable".
    // eslint-disable-next-line no-console
    console.warn("[config] /api/config unavailable, running in degraded mode", err?.message);
    return {};
  }
}

/**
 * Returns a promise that resolves to the app-config object. Safe to call
 * from anywhere and at any time. The promise is created lazily on first
 * call so we don't fire a request during SSR / test bundling.
 */
export function getAppConfig() {
  if (_configPromise === null) {
    _configPromise = _fetchConfig();
  }
  return _configPromise;
}

/**
 * Force a fresh fetch on the next `getAppConfig()` call — used by tests
 * or if the backend hot-swaps config at runtime. Not called in prod.
 */
export function resetAppConfig() {
  _configPromise = null;
}

/**
 * Convenience getter for just the VAPID public key. Callers that only
 * need this one field don't have to destructure the whole config.
 */
export async function getVapidPublicKey() {
  const cfg = await getAppConfig();
  return cfg.vapid_public_key || null;
}

/**
 * Eagerly kick off the fetch. Import this in `App.js` so the round-trip
 * happens while React is still mounting, and by the time any component
 * needs the VAPID key it's already resolved.
 */
export function preloadAppConfig() {
  return getAppConfig();
}
